import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthRedirect } from '../components/AuthRedirect';
import { BackButton } from '../components/BackButton';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { HelpTooltip } from '../components/HelpTooltip';
import { HCaptcha } from '../components/HCaptcha';
import { APP_CONFIG } from '../config/constants';

interface UnifiedSignupPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const UnifiedSignupPage: React.FC<UnifiedSignupPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { signUp, signOut, loading } = useAuth();
  
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    filiere: '',
    annee: '',
    isTutor: false,
    // Champs tuteur (conditionnels)
    tarif: 30,
    matieres: [] as string[],
    disponibilites: {} as Record<string, string[]>,
    email_contact: '',
    acceptTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const filieres = [
    { value: 'IG', label: 'Informatique de Gestion (IG)' },
    { value: 'EE', label: 'Économie d\'Entreprise (EE)' },
    { value: 'IBM', label: 'International Business Management (IBM)' },
    { value: 'IS', label: 'Information Science (IS)' },
    { value: 'Autre', label: 'Autre' }
  ];

  // Matières par filière
  const subjectsByFiliere: Record<string, string[]> = {
    IBM: ['General Management', 'Accounting & Finance', 'Marketing & Communication', 'Leadership & Soft-skills', 'Economics', 'Business Law', 'Business Tools', 'Mathématiques'],
    IG: ['Programmation', 'Base de données', 'Réseaux & Sécurité', 'Gestion de projet', 'Analyse & Modélisation', 'Web Development', 'Mathématiques'],
    IS: ['Bibliothéconomie', 'Archivistique', 'Technologies Web', 'UX Design', 'Gestion de données', 'Recherche d\'information', 'Mathématiques'],
    EE: ['Management', 'Comptabilité', 'Finance', 'Marketing', 'Ressources Humaines', 'Economics', 'Droit des affaires', 'Mathématiques']
  };

  const jours = [
    { key: 'lundi', label: 'Lundi' },
    { key: 'mardi', label: 'Mardi' },
    { key: 'mercredi', label: 'Mercredi' },
    { key: 'jeudi', label: 'Jeudi' },
    { key: 'vendredi', label: 'Vendredi' },
    { key: 'samedi', label: 'Samedi' },
    { key: 'dimanche', label: 'Dimanche' }
  ];

  const creneaux = [
    { key: 'matin', label: 'Matin (8h-12h)' },
    { key: 'apm', label: 'Après-midi (12h-18h)' },
    { key: 'soir', label: 'Soir (18h-22h)' }
  ];


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'tarif' ? Number(value) : value)
    }));

    // Réinitialiser les matières si filière change
    if (name === 'filiere') {
      setFormData(prev => ({ ...prev, matieres: [] }));
    }
  };

  const toggleMatiere = (matiere: string) => {
    setFormData(prev => ({
      ...prev,
      matieres: prev.matieres.includes(matiere)
        ? prev.matieres.filter(m => m !== matiere)
        : [...prev.matieres, matiere]
    }));
  };

  const handleDisponibiliteChange = (jour: string, creneau: string, checked: boolean) => {
    setFormData(prev => {
      const newDispos = { ...prev.disponibilites };
      
      if (!newDispos[jour]) {
        newDispos[jour] = [];
      }
      
      if (checked) {
        if (!newDispos[jour].includes(creneau)) {
          newDispos[jour] = [...newDispos[jour], creneau];
        }
      } else {
        newDispos[jour] = newDispos[jour].filter(c => c !== creneau);
        if (newDispos[jour].length === 0) {
          delete newDispos[jour];
        }
      }
      
      return { ...prev, disponibilites: newDispos };
    });
  };

  const validateForm = () => {
    // Validation des champs de base
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      showToast('Veuillez remplir tous les champs obligatoires', 'error');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showToast('Veuillez entrer une adresse email valide', 'error');
      return false;
    }

    // Vérifier que l'email est un email institutionnel HEG/HES-SO
    const emailLower = formData.email.toLowerCase();
    const isValidDomain = APP_CONFIG.validEmailDomains.some(domain => emailLower.endsWith(domain));
    
    if (!isValidDomain) {
      showToast('Veuillez utiliser votre adresse email étudiant-e (@etu.hesge.ch)', 'error');
      return false;
    }

    if (formData.password.length < 8) {
      showToast('Le mot de passe doit contenir au moins 8 caractères', 'error');
      return false;
    }

    // Validation mot de passe fort (requis par Supabase)
    if (!/[a-z]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins une minuscule (a-z)', 'error');
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins une majuscule (A-Z)', 'error');
      return false;
    }

    if (!/[0-9]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins un chiffre (0-9)', 'error');
      return false;
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins un caractère spécial (!@#$...)', 'error');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      showToast('Les mots de passe ne correspondent pas', 'error');
      return false;
    }

    if (!formData.filiere || !formData.annee) {
      showToast('Veuillez renseigner votre filière et année d\'études', 'error');
      return false;
    }

    // Validation spécifique tuteur (seulement si case cochée)
    if (formData.isTutor) {
      if (!formData.email_contact) {
        showToast('Email de contact obligatoire pour les tuteur-trices', 'error');
        return false;
      }

      if (formData.matieres.length === 0) {
        showToast('Veuillez sélectionner au moins une matière', 'error');
        return false;
      }

      if (Object.keys(formData.disponibilites).length === 0) {
        showToast('Veuillez sélectionner au moins une disponibilité', 'error');
        return false;
      }

      if (formData.tarif < 10 || formData.tarif > 100) {
        showToast('Le tarif doit être entre 10 et 100 CHF/h', 'error');
        return false;
      }
    }

    if (!formData.acceptTerms) {
      showToast('Veuillez accepter les conditions d\'utilisation', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!captchaToken) {
      showToast('Veuillez valider le captcha de sécurité', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Créer le compte utilisateur
      const { user, error: signUpError } = await signUp(formData.email, formData.password, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        captchaToken, // Protection anti-bot
      });

      if (signUpError) {
        let errorMessage = 'Erreur lors de l\'inscription';
        
        switch (signUpError.message) {
          case 'User already registered':
            errorMessage = 'Cette adresse email est déjà utilisée';
            break;
          case 'Password should be at least 6 characters':
            errorMessage = 'Le mot de passe doit contenir au moins 6 caractères';
            break;
          default:
            // Gérer l'erreur de mot de passe faible de Supabase
            if (signUpError.message.includes('Password should contain at least one character')) {
              errorMessage = 'Le mot de passe doit contenir : minuscules, majuscules, chiffres ET caractères spéciaux (!@#$...)';
            } else {
              errorMessage = signUpError.message;
            }
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      if (user) {
        
        // Sauvegarder temporairement les informations du profil pour créer après confirmation
        if (formData.isTutor) {
          localStorage.setItem('pendingTutorProfile', 'true');
        }
        
        // 🔒 CRITIQUE : Supabase crée une session automatiquement après signUp
        // On DOIT se déconnecter immédiatement pour forcer la confirmation d'email
        await signOut();
        
        showToast('Compte créé ! Vérifiez votre email pour confirmer votre inscription avant de vous connecter.', 'success');
        
        // Rediriger vers la page de login (pas vers email-not-confirmed car plus de session)
        navigate('/login', { 
          state: { 
            message: 'Vérifiez votre email et confirmez votre inscription avant de vous connecter.' 
          } 
        });
        return;
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthRedirect showToast={showToast} showMessage="Vous êtes déjà connecté-e ! Redirection vers votre profil.">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Navigation */}
          <div className="mb-6">
            <BackButton to="/" label="Retour à l'accueil" />
            <Breadcrumbs />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-user-plus text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Créer votre compte
            </h2>
            <p className="text-gray-600">
              Une inscription, toutes les possibilités
            </p>
          </div>


          {/* Formulaire */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-user text-[#002c46] mr-2"></i>
                  Informations personnelles
                  <HelpTooltip 
                    content="Ces informations serviront à créer votre compte et votre profil sur la plateforme."
                    className="ml-2"
                  />
                </h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                      placeholder="Dupont"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                    placeholder="prenom.nom@etu.hesge.ch"
                  />
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-gray-700">
                      <i className="fas fa-graduation-cap text-green-600 mr-1"></i>
                      <strong>Utilise ton adresse email étudiant-e HEG :</strong>
                    </p>
                    <p className="text-xs text-gray-600 mt-1 ml-5">
                      • Format : (prénom).(nom)@etu.hesge.ch
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mot de passe *
                    </label>
                    <input
                      name="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirmer le mot de passe *
                    </label>
                    <input
                      name="confirmPassword"
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {/* Indicateur de validation mot de passe */}
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    <i className="fas fa-shield-alt text-[#002c46] mr-1"></i>
                    Votre mot de passe doit contenir :
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-5">
                    <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                      <i className={`fas ${formData.password.length >= 8 ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Au moins 8 caractères
                    </li>
                    <li className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[a-z]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Une minuscule (a-z)
                    </li>
                    <li className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[A-Z]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Une majuscule (A-Z)
                    </li>
                    <li className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[0-9]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Un chiffre (0-9)
                    </li>
                    <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Un caractère spécial (!@#$...)
                    </li>
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone (optionnel)
                  </label>
                  <input
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>
              </div>

              {/* Informations académiques */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  <i className="fas fa-graduation-cap text-[#002c46] mr-2"></i>
                  Informations académiques
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Filière d'études *
                    </label>
                    <select
                      name="filiere"
                      required
                      value={formData.filiere}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                    >
                      <option value="">Sélectionner une filière</option>
                      {filieres.map(filiere => (
                        <option key={filiere.value} value={filiere.value}>{filiere.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Année d'études *
                    </label>
                    <select
                      name="annee"
                      required
                      value={formData.annee}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                    >
                      <option value="">Sélectionner</option>
                      <option value="1">Année académique : 1</option>
                      <option value="2">Année académique : 2</option>
                      <option value="3">Année académique : 3</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Option tuteur */}
              <div>
                <div className="flex items-center mb-4">
                  <input
                    id="isTutor"
                    name="isTutor"
                    type="checkbox"
                    checked={formData.isTutor}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded"
                  />
                  <label htmlFor="isTutor" className="ml-3 text-lg font-semibold text-gray-900">
                    <i className="fas fa-chalkboard-teacher text-green-600 mr-2"></i>
                    Je souhaite aussi être tuteur-trice
                  </label>
                </div>

                {/* Champs tuteur conditionnels */}
                {formData.isTutor && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-6">
                    <h4 className="text-lg font-semibold text-green-900 mb-4">
                      Informations tuteur-trice
                    </h4>

                    {/* Email de contact */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email de contact *
                        <HelpTooltip 
                          content="Email qui sera partagé avec les étudiant-es après un match. Peut être différent de votre email de connexion."
                          className="ml-2"
                        />
                      </label>
                      <input
                        name="email_contact"
                        type="email"
                        required={formData.isTutor}
                        value={formData.email_contact}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                        placeholder="contact@exemple.com"
                      />
                    </div>

                    {/* Tarif */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tarif horaire (CHF/h) *
                        <HelpTooltip 
                          content="Tarif recommandé : 20-50 CHF/h selon votre expérience. Modifiable plus tard."
                          className="ml-2"
                        />
                      </label>
                      <input
                        name="tarif"
                        type="number"
                        min="10"
                        max="100"
                        required={formData.isTutor}
                        value={formData.tarif}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Entre 10 et 100 CHF/h</p>
                    </div>

                    {/* Matières */}
                    {formData.filiere && subjectsByFiliere[formData.filiere] && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Matières enseignées *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {subjectsByFiliere[formData.filiere].map((matiere) => (
                            <label key={matiere} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={formData.matieres.includes(matiere)}
                                onChange={() => toggleMatiere(matiere)}
                                className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{matiere}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Disponibilités */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Disponibilités *
                        <HelpTooltip 
                          content="Indiquez vos créneaux habituels. Modifiables à tout moment."
                          className="ml-2"
                        />
                      </label>
                      <div className="grid gap-3">
                        {jours.map(jour => (
                          <div key={jour.key} className="border border-gray-200 rounded-lg p-3">
                            <h5 className="font-medium text-gray-900 mb-2">{jour.label}</h5>
                            <div className="grid grid-cols-3 gap-2">
                              {creneaux.map(creneau => (
                                <label key={creneau.key} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={formData.disponibilites[jour.key]?.includes(creneau.key) || false}
                                    onChange={(e) => handleDisponibiliteChange(jour.key, creneau.key, e.target.checked)}
                                    className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{creneau.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Conditions */}
              <div className="flex items-center">
                <input
                  id="acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  required
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded"
                />
                <label htmlFor="acceptTerms" className="ml-3 text-sm text-gray-700">
                  J'accepte les{' '}
                  <a href="/conditions" target="_blank" className="text-blue-600 hover:text-blue-800 underline">
                    conditions d'utilisation
                  </a>
                  {' '}et la{' '}
                  <a href="/confidentialite" target="_blank" className="text-blue-600 hover:text-blue-800 underline">
                    politique de confidentialité
                  </a>
                </label>
              </div>

              {/* hCaptcha - Protection anti-bot */}
              <div className="space-y-3">
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  captchaToken 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {!captchaToken && (
                    <HCaptcha
                      onVerify={(token) => {
                        setCaptchaToken(token);
                      }}
                      onExpire={() => {
                        setCaptchaToken('');
                      }}
                      onError={() => {
                        setCaptchaToken('');
                        showToast('Erreur de vérification. Veuillez réessayer.', 'error');
                      }}
                    />
                  )}
                  
                  {captchaToken ? (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-3">
                        <i className="fas fa-check text-white text-3xl"></i>
                      </div>
                      <p className="text-lg font-bold text-green-700 mb-1">
                        ✅ Vérification réussie !
                      </p>
                      <p className="text-sm text-green-600">
                        Vous pouvez maintenant créer votre compte
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 text-center mt-2">
                      <i className="fas fa-shield-alt mr-1"></i>
                      Protection contre les robots automatiques
                    </p>
                  )}
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !captchaToken}
                  className="flex-1 py-3 px-6 bg-[#002c46] text-white rounded-lg hover:bg-[#001a2a] transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!captchaToken ? 'Veuillez valider le captcha pour continuer' : ''}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Création en cours...
                    </>
                  ) : !captchaToken ? (
                    <>
                      <i className="fas fa-lock mr-2"></i>
                      Validez le captcha pour continuer
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-plus mr-2"></i>
                      {formData.isTutor ? 'Créer mon compte tuteur-trice' : 'Créer mon compte'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthRedirect>
  );
};
